<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { type RouteLocationRaw, useRouter } from 'vue-router';
import {
  AccessVisibility,
  type ContractManagementViewInDb,
  type ContractManagementViewUpdate,
} from '@/api';
import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import { useListQuerySync } from '@/composables/useListQuerySync';
import { useRowDelete } from '@/composables/useRowDelete';
import { logger } from '@/utils/logger';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaAddButton from '@/components/design/base/GaiaAddButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaCountPill from '@/components/design/base/GaiaCountPill.vue';
import GaiaCreateButton from '@/components/design/base/GaiaCreateButton.vue';
import GaiaDropdown, { gaiaDangerOption } from '@/components/design/base/GaiaDropdown.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaPopover from '@/components/design/base/GaiaPopover.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaTag from '@/components/design/base/GaiaTag.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaTextarea from '@/components/design/base/inputs/GaiaTextarea.vue';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaTableSkeleton from '@/components/design/composite/GaiaTableSkeleton.vue';
import GaiaDataTable, {
  type GaiaDataColumn,
} from '@/components/design/composite/GaiaDataTable.vue';
import GaiaEmptyState from '@/components/design/composite/GaiaEmptyState.vue';
import GaiaFilterBar from '@/components/design/composite/GaiaFilterBar.vue';
import GaiaFilterChip from '@/components/design/composite/GaiaFilterChip.vue';
import GaiaFilterValuePanel, {
  type GaiaFilterValue,
} from '@/components/design/composite/GaiaFilterValuePanel.vue';
import GaiaPagination from '@/components/design/composite/GaiaPagination.vue';
import GaiaTableRowState from '@/components/design/composite/GaiaTableRowState.vue';
import GaiaToggleRow from '@/components/design/composite/GaiaToggleRow.vue';
import { gaiaContentWidth, gaiaNeutral } from '@/components/design/themes/tokens';
import { useGaiaToast } from '@/composables/useGaiaToast';

/**
 * Views list — the CM Views mock. A view is a saved spreadsheet over
 * contracts, so the page is the same list anatomy the entity lists use: title +
 * count, search, a single-facet Filter, and one primary action.
 *
 * Search, sort, paging and filters all stay server-side and URL-synced exactly
 * as before; what changed is the chrome (Gaia data card instead of
 * AppDataTable) and that edit/delete moved from row icons into the row ⋯ menu
 * with inline confirm/busy strips instead of a popconfirm and a toast.
 */

const router = useRouter();
const message = useGaiaToast();

const {
  currentPage,
  pageSize,
  searchQuery,
  sortKey,
  sortDirection,
  sortActivated,
  columnFilters,
  setColumnFilters,
} = useListQuerySync();

const views = ref<ContractManagementViewInDb[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);

// --- filter bar ------------------------------------------------------------
const filterOptions = ref<Record<string, (string | null)[]>>({});
const filterBarOpen = ref<boolean>(false);
const openFilterKeys = ref<string[]>([]);
const openChip = ref<string | null>(null);
const valueSearch = ref<Record<string, string>>({});

/**
 * Facets the mock offers. A facet only appears once the backend has actually
 * reported values for it in `filter_options` — declaring one the search
 * endpoint doesn't group by would render a chip that filters nothing.
 */
const FILTER_DEFS = [
  { key: 'visibility', label: 'Visibility' },
  { key: 'owner_user', label: 'Owner' },
] as const;

const filterDefs = computed(() => FILTER_DEFS.filter(filter => filter.key in filterOptions.value));

// --- row state -------------------------------------------------------------
const rowDelete = useRowDelete<ContractManagementViewInDb>();

// --- edit dialog -----------------------------------------------------------
const editing = ref<ContractManagementViewInDb | null>(null);
const formName = ref<string>('');
const formDescription = ref<string>('');
const formVisible = ref<boolean>(false);
const formError = ref<string | null>(null);
const saving = ref<boolean>(false);
const saveError = ref<string | null>(null);

// --- derived ---------------------------------------------------------------
const hasQuery = computed(() => searchQuery.value.trim().length > 0);

const activeFilterCount = computed(() =>
  Object.values(columnFilters.value).reduce((total, values) => total + values.length, 0)
);

const countLabel = computed(() => {
  if (loading.value) return 'Loading';
  return `${totalCount.value} ${totalCount.value === 1 ? 'view' : 'views'}`;
});

const showEmpty = computed(
  () =>
    !loading.value &&
    !loadError.value &&
    views.value.length === 0 &&
    !hasQuery.value &&
    !activeFilterCount.value
);
const showNoMatch = computed(
  () => !loading.value && !loadError.value && views.value.length === 0 && !showEmpty.value
);
const showRows = computed(() => !loading.value && !loadError.value && views.value.length > 0);

const noMatchTitle = computed(() =>
  hasQuery.value ? `No views match “${searchQuery.value.trim()}”` : 'No views match the filter'
);

const noMatchBody = computed(
  () =>
    `Search covers view names and descriptions${
      activeFilterCount.value ? '; filters are also active' : ''
    }. Clear it to see every view.`
);

const skeletonCells = [
  '62%',
  '80%',
  { width: '58px', shape: 'chip' },
  { width: '24px', shape: 'avatar' },
  '110px',
  '110px',
] as const;

/** Column set and tracks are the mock's own — the toolbar offers no column menu. */
const tableColumns: GaiaDataColumn[] = [
  { key: 'name', label: 'View name', width: 'minmax(190px,1.15fr)', sortable: true },
  { key: 'description', label: 'Description', width: 'minmax(170px,1.3fr)' },
  { key: 'visibility', label: 'Visibility', width: '100px' },
  { key: 'owner_user', label: 'Owner', width: '64px' },
  { key: 'created_at', label: 'Created', width: '168px', sortable: true },
  { key: 'updated_at', label: 'Updated', width: '168px' },
];

const rowMenuOptions = [
  { key: 'open', label: 'Open view' },
  { key: 'edit', label: 'Edit details' },
  { key: 'divider', type: 'divider' },
  { key: 'delete', label: 'Delete view', props: gaiaDangerOption },
];

/**
 * The dialog's header text is captured when it opens rather than derived from
 * `editing`. Closing clears `editing`, and a computed header would blank out
 * while the dialog is still on screen leaving — the title would read `Edit ""`.
 */
const editTitle = ref<string>('');
const editSubtitle = ref<string>('');
const editFootNote = ref<string>('');

// --- data ------------------------------------------------------------------
/**
 * Filter values arrive as display strings; "(Empty)" stands in for a null the
 * backend grouped on, so it has to travel back as an empty string.
 */
function buildColumnFilters(): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  for (const [key, values] of Object.entries(columnFilters.value)) {
    if (Array.isArray(values) && values.length > 0) {
      filters[key] = values.map(value => (value === '(Empty)' ? '' : value));
    }
  }
  return filters;
}

async function fetchViews(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    const result = await ContractViewsService.search({
      text: searchQuery.value || '',
      page: currentPage.value,
      pageSize: pageSize.value,
      sortKey: sortKey.value,
      sortDirection: sortDirection.value,
      columnFilters: buildColumnFilters(),
    });
    views.value = result.results;
    totalCount.value = result.result_count;
    filterOptions.value = result.filter_options as Record<string, (string | null)[]>;
  } catch (error) {
    logger.error('Error fetching views:', error);
    loadError.value = 'Couldn’t load views';
  } finally {
    loading.value = false;
  }
}

// A new search starts from the first page and drops any manual sort. The field
// debounces its own keystrokes and fires this at once when it is cleared.
async function applySearch(): Promise<void> {
  currentPage.value = 1;
  sortActivated.value = false;
  await fetchViews();
}

watch(currentPage, () => {
  if (currentPage.value > 0) void fetchViews();
});

watch(pageSize, () => {
  if (pageSize.value > 0) void fetchViews();
});

watch(columnFilters, () => {
  currentPage.value = 1;
  void fetchViews();
});

onMounted(() => {
  void fetchViews();
});

// --- formatting ------------------------------------------------------------
const emptyCell = '—';

function formatDateTime(raw: string | null | undefined): string {
  if (!raw) return emptyCell;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return emptyCell;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isPrivate(view: ContractManagementViewInDb): boolean {
  return view.visibility === AccessVisibility.PRIVATE;
}

function ownerLabel(view: ContractManagementViewInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand; empty string must use the fallback
  return view.owner_user?.full_name || view.owner_user?.email || 'Unassigned';
}

/** Two letters from the owner's name, or the mail local part when there is none. */
function ownerInitials(view: ContractManagementViewInDb): string {
  const name = (view.owner_user?.full_name ?? '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  const email = (view.owner_user?.email ?? '').trim();
  if (email) return (email.split('@')[0] || '').slice(0, 2).toUpperCase() || '?';
  return '?';
}

// --- toolbar ---------------------------------------------------------------
/**
 * One control for both jobs, as the mock draws it: opening seeds the bar with
 * the first facet, closing it drops every active filter.
 */
function toggleFilterBar(): void {
  const on = !(filterBarOpen.value || activeFilterCount.value > 0);
  if (!on) {
    filterBarOpen.value = false;
    openFilterKeys.value = [];
    openChip.value = null;
    setColumnFilters({});
    return;
  }
  filterBarOpen.value = true;
  if (!openFilterKeys.value.length && filterDefs.value.length > 0) {
    const first = filterDefs.value[0];
    openFilterKeys.value = [first.key];
    openChip.value = first.key;
  }
}

const availableFilters = computed(() =>
  filterDefs.value.filter(filter => !openFilterKeys.value.includes(filter.key))
);

function addFilter(key: string): void {
  openFilterKeys.value = [...openFilterKeys.value, key];
  openChip.value = key;
}

function removeFilter(key: string): void {
  openFilterKeys.value = openFilterKeys.value.filter(open => open !== key);
  openChip.value = null;
  setColumnFilters({ ...columnFilters.value, [key]: [] });
}

function toggleFilterValue(key: string, value: string): void {
  const current = columnFilters.value[key] ?? [];
  const next = current.includes(value)
    ? current.filter(picked => picked !== value)
    : [...current, value];
  setColumnFilters({ ...columnFilters.value, [key]: next });
}

function clearAllFilters(): void {
  setColumnFilters({});
}

function clearSearchAndFilters(): void {
  searchQuery.value = '';
  currentPage.value = 1;
  setColumnFilters({});
  filterBarOpen.value = false;
  openFilterKeys.value = [];
  openChip.value = null;
  void fetchViews();
}

function filterLabel(key: string): string {
  return filterDefs.value.find(filter => filter.key === key)?.label ?? key;
}

function filterValues(key: string): GaiaFilterValue[] {
  const term = (valueSearch.value[key] ?? '').trim().toLowerCase();
  const picked = columnFilters.value[key] ?? [];
  return (filterOptions.value[key] ?? [])
    .map(value => value ?? '(Empty)')
    .filter(value => !term || value.toLowerCase().includes(term))
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ key: value, label: value, checked: picked.includes(value) }));
}

function onSort(key: string): void {
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    // Dates open newest-first; a name opens A–Z.
    sortDirection.value = key === 'created_at' ? 'desc' : 'asc';
  }
  currentPage.value = 1;
  void fetchViews();
}

// --- row actions -----------------------------------------------------------
function detailRoute(view: ContractManagementViewInDb): RouteLocationRaw {
  return {
    name: 'contract-management-view-detail',
    params: { viewId: view.uid },
  };
}

function goCreate(): void {
  void router.push({ name: 'contract-management-view-create' });
}

function onRowMenu(key: string, view: ContractManagementViewInDb): void {
  if (key === 'open') {
    void router.push(detailRoute(view));
    return;
  }
  if (key === 'edit') {
    openEdit(view);
    return;
  }
  rowDelete.arm(view);
}

async function deleteView(view: ContractManagementViewInDb): Promise<void> {
  await rowDelete.run(view, {
    work: async () => {
      await ContractViewsService.delete(view.uid);
      message.success('View deleted');
      await fetchViews();
    },
    errorText: () => 'Couldn’t delete this view. Nothing was changed.',
  });
}

// --- edit ------------------------------------------------------------------
function openEdit(view: ContractManagementViewInDb): void {
  editing.value = view;
  editTitle.value = view.name;
  editSubtitle.value = `Created ${formatDateTime(view.created_at)}`;
  editFootNote.value = view.updated_at ? `Last saved ${formatDateTime(view.updated_at)}` : '';
  formName.value = view.name;
  formDescription.value = view.description ?? '';
  formVisible.value = view.visibility === AccessVisibility.VISIBLE;
  formError.value = null;
  saveError.value = null;
}

function closeEdit(): void {
  if (saving.value) return;
  editing.value = null;
}

async function saveEdit(): Promise<void> {
  const view = editing.value;
  if (!view || saving.value) return;

  const name = formName.value.trim();
  if (!name) {
    formError.value = 'Please enter a view name';
    return;
  }

  saving.value = true;
  formError.value = null;
  saveError.value = null;
  try {
    const payload: ContractManagementViewUpdate = {
      name,
      description: formDescription.value.trim() || null,
      visibility: formVisible.value ? AccessVisibility.VISIBLE : AccessVisibility.PRIVATE,
    };
    await ContractViewsService.update(view.uid, payload);
    message.success('View updated');
    editing.value = null;
    await fetchViews();
  } catch (error) {
    logger.error('Error updating view:', error);
    saveError.value = 'Failed to update view';
  } finally {
    saving.value = false;
  }
}

// --- chrome ----------------------------------------------------------------
const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.contractManagement }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.views }}</n-breadcrumb-item>
    </template>
    <n-flex vertical :size="18">
      <!-- Title + count, with the toolbar pushed right. All three controls sit
           on the mock's 36px step. -->
      <n-flex align="flex-start" :size="[20, 12]">
        <n-flex align="baseline" :size="10" :style="{ minWidth: 0 }">
          <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
            Views
          </gaia-header>
          <gaia-count-label>{{ countLabel }}</gaia-count-label>
        </n-flex>

        <n-flex align="center" justify="end" :size="8" :style="{ marginLeft: 'auto' }">
          <gaia-search-input
            v-model:value="searchQuery"
            placeholder="Search views"
            :debounce="500"
            @search="applySearch"
          />

          <gaia-button v-if="filterDefs.length" size="medium" @click="toggleFilterBar">
            <template #icon>
              <svg
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              >
                <path d="M2 4h12M4.5 8h7M7 12h2" />
              </svg>
            </template>
            Filter
            <gaia-count-pill v-if="activeFilterCount" :style="{ marginLeft: '7px' }">
              {{ activeFilterCount }}
            </gaia-count-pill>
          </gaia-button>

          <gaia-create-button subject="view" data-tour="views.new-button" @click="goCreate" />
        </n-flex>
      </n-flex>

      <!-- One chip per opened facet, each with its value panel. -->
      <gaia-filter-bar
        v-if="filterBarOpen || activeFilterCount"
        :can-add="availableFilters.length > 0"
        :show-reset="activeFilterCount > 0"
        @reset="clearAllFilters"
      >
        <gaia-popover
          v-for="key in openFilterKeys"
          :key="key"
          placement="bottom-start"
          :show="openChip === key"
          @update:show="(show: boolean) => (openChip = show ? key : null)"
        >
          <template #trigger>
            <gaia-filter-chip
              :field="filterLabel(key)"
              :values="(columnFilters[key] ?? []).slice(0, 3)"
              :overflow="
                (columnFilters[key] ?? []).length > 3
                  ? `+${(columnFilters[key] ?? []).length - 3}`
                  : undefined
              "
              :active="openChip === key"
              @toggle="openChip = openChip === key ? null : key"
              @remove="removeFilter(key)"
              @remove-value="(value: string) => toggleFilterValue(key, value)"
            />
          </template>
          <gaia-filter-value-panel
            :values="filterValues(key)"
            :search="valueSearch[key] ?? ''"
            :picked-label="
              (columnFilters[key] ?? []).length
                ? `${(columnFilters[key] ?? []).length} ${(columnFilters[key] ?? []).length === 1 ? 'value' : 'values'} selected`
                : undefined
            "
            @update:search="(term: string) => (valueSearch[key] = term)"
            @toggle="(value: string) => toggleFilterValue(key, value)"
            @clear="setColumnFilters({ ...columnFilters, [key]: [] })"
          />
        </gaia-popover>

        <template #add>
          <gaia-dropdown
            :options="availableFilters.map(filter => ({ key: filter.key, label: filter.label }))"
            placement="bottom-start"
            @select="addFilter"
          >
            <gaia-add-button subject="filter" variant="dashed" />
          </gaia-dropdown>
        </template>
      </gaia-filter-bar>

      <gaia-data-table
        data-tour="views.list-table"
        :columns="tableColumns"
        :sort-key="sortKey"
        :sort-direction="sortDirection === 'asc' ? 'asc' : 'desc'"
        :sort-active="sortActivated"
        :action-width="56"
        @sort="onSort"
      >
        <template #default="{ rowStyle }">
          <!-- Loading: toolbar and header stay live, the body shimmers. -->
          <gaia-table-skeleton
            v-if="loading"
            :row-style="rowStyle"
            :cells="skeletonCells"
            :rows="6"
          />

          <gaia-empty-state v-else-if="loadError" status="alert" :title="loadError">
            The server didn’t respond in time. Nothing was changed — retrying is safe.
            <template #actions>
              <gaia-button type="primary" size="small" @click="fetchViews">Retry</gaia-button>
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showEmpty" title="No views yet">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M2.5 3.5h13v11h-13z" />
                <path d="M2.5 7h13M7 7v7.5" />
              </svg>
            </template>
            A view is a saved spreadsheet over your contracts — pick folders and contracts, choose
            columns, and ask AI questions across every contract at once.
            <template #actions>
              <gaia-create-button subject="view" :icon="false" @click="goCreate" />
            </template>
          </gaia-empty-state>

          <gaia-empty-state v-else-if="showNoMatch" :title="noMatchTitle">
            <template #icon>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              >
                <circle cx="8" cy="8" r="5" />
                <path d="M11.8 11.8 15.5 15.5" />
              </svg>
            </template>
            {{ noMatchBody }}
            <template #actions>
              <gaia-button size="small" @click="clearSearchAndFilters">
                Clear search &amp; filters
              </gaia-button>
            </template>
          </gaia-empty-state>

          <template v-else-if="showRows">
            <template v-for="view in views" :key="view.uid">
              <!-- A row in trouble is replaced by its strip, never stacked with it. -->
              <gaia-table-row-state
                v-if="rowDelete.stateOf(view.uid) === 'confirm'"
                variant="confirm"
                :title="`Delete “${view.name}”?`"
                @cancel="rowDelete.cancel()"
                @confirm="deleteView(view)"
              >
                The view definition is removed; the contracts stay.
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(view.uid) === 'busy'"
                variant="busy"
                note="Undo not available"
              >
                Deleting “{{ view.name }}”…
              </gaia-table-row-state>

              <gaia-table-row-state
                v-else-if="rowDelete.stateOf(view.uid) === 'error'"
                variant="error"
                @retry="deleteView(view)"
                @dismiss="rowDelete.dismissError()"
              >
                {{ rowDelete.errorTextOf(view.uid) }}
              </gaia-table-row-state>

              <div v-else class="row" :style="rowStyle" @click="router.push(detailRoute(view))">
                <div class="cell">
                  <gaia-text truncate size="body" weight="semibold" tone="ink">
                    <router-link
                      :to="detailRoute(view)"
                      style="color: inherit; text-decoration: none"
                      @click.stop
                    >
                      {{ view.name }}
                    </router-link>
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text truncate size="body" tone="tertiary">
                    {{ view.description || emptyCell }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-tag :variant="isPrivate(view) ? 'amber' : 'default'">
                    {{ isPrivate(view) ? 'Private' : 'Visible' }}
                  </gaia-tag>
                </div>

                <div class="cell">
                  <n-tooltip placement="top" :delay="200">
                    <template #trigger>
                      <gaia-avatar :size="26">{{ ownerInitials(view) }}</gaia-avatar>
                    </template>
                    {{ ownerLabel(view) }}
                  </n-tooltip>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="dense"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDateTime(view.created_at) }}
                  </gaia-text>
                </div>

                <div class="cell">
                  <gaia-text
                    truncate
                    size="dense"
                    tone="tertiary"
                    :style="{ fontVariantNumeric: 'tabular-nums' }"
                  >
                    {{ formatDateTime(view.updated_at) }}
                  </gaia-text>
                </div>

                <n-flex justify="end" :size="0" :wrap="false">
                  <gaia-dropdown
                    :options="rowMenuOptions"
                    placement="bottom-end"
                    @select="(key: string) => onRowMenu(key, view)"
                  >
                    <gaia-icon-button :dimension="28" quaternary title="More" @click.stop>
                      ⋯
                    </gaia-icon-button>
                  </gaia-dropdown>
                </n-flex>
              </div>
            </template>
          </template>
        </template>

        <template v-if="showRows" #footer>
          <gaia-pagination
            :page="currentPage"
            :page-size="pageSize"
            :total="totalCount"
            @update:page="(page: number) => (currentPage = page)"
            @update:page-size="(size: number) => (pageSize = size)"
          />
        </template>
      </gaia-data-table>
    </n-flex>

    <!-- Edit details. Name, description and who can open it — the view's
         columns are edited on the detail page, not here. -->
    <gaia-modal
      :show="editing !== null"
      width="460px"
      :closable="false"
      content-style="padding: 20px"
      @update:show="(value: boolean) => !value && closeEdit()"
    >
      <n-flex align="flex-start" :size="12" :wrap="false">
        <div style="min-width: 0">
          <gaia-text size="subtitle" weight="semibold" tone="ink">
            Edit “{{ editTitle }}”
          </gaia-text>
          <gaia-text size="micro" tone="muted" :style="{ display: 'block', marginTop: '6px' }">
            {{ editSubtitle }}
          </gaia-text>
        </div>
        <gaia-icon-button
          :dimension="26"
          quaternary
          title="Close"
          :style="{ marginLeft: 'auto' }"
          @click="closeEdit"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </gaia-icon-button>
      </n-flex>

      <gaia-banner v-if="saveError" plain type="error" :style="{ marginTop: '14px' }">
        {{ saveError }}
      </gaia-banner>

      <n-flex vertical :size="14" :style="{ marginTop: '16px' }">
        <gaia-form-field label="Name" required :error="formError || undefined">
          <gaia-input
            :value="formName"
            size="medium"
            :status="formError ? 'error' : undefined"
            style="width: 100%"
            @update:value="
              (value: string) => {
                formName = value;
                formError = null;
              }
            "
          />
        </gaia-form-field>

        <gaia-form-field label="Description" hint="Optional">
          <gaia-textarea
            :value="formDescription"
            placeholder="What this view tracks, and for whom"
            :autosize="{ minRows: 3 }"
            style="width: 100%"
            @update:value="(value: string) => (formDescription = value)"
          />
        </gaia-form-field>

        <gaia-toggle-row
          card
          label="Visible to others in the organisation"
          :hint="
            formVisible
              ? 'Everyone in the organisation can open this view.'
              : 'Only you can open this view.'
          "
          :checked="formVisible"
          :disabled="saving"
          @update:checked="(value: boolean) => (formVisible = value)"
        />
      </n-flex>

      <n-flex
        align="center"
        :size="8"
        :wrap="false"
        :style="{ marginTop: '18px', paddingTop: '14px' }"
      >
        <gaia-text size="micro" tone="muted">{{ editFootNote }}</gaia-text>
        <gaia-button size="small" :style="{ marginLeft: 'auto' }" @click="closeEdit">
          Cancel
        </gaia-button>
        <gaia-button type="primary" size="small" :loading="saving" @click="saveEdit">
          {{ saving ? 'Saving…' : 'Save changes' }}
        </gaia-button>
      </n-flex>
    </gaia-modal>
  </Layout>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  height: 56px;
  padding: 0 18px;
  border-bottom: 1px solid v-bind(rowBorder);
  cursor: pointer;
}

.row:hover {
  background: v-bind(rowHover);
}

.cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
</style>
